
class CartPersistenceConfig {
    static CUSTOMER_ID = window.__st?.cid ? String(window.__st.cid) : null;

    static GET_ENDPOINT = 'https://social.samaya.mx/api/shasa/cart-get';
    static UPDATE_ENDPOINT = 'https://social.samaya.mx/api/shasa/cart-update';

    static HEADERS = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Shasa-Token': 'sha-tk-ysx5Yl3Vss7ScUZvsVpT',
        'X-Sha-Tk': 'sha-20012607-samyaz',
    };

    static ATTRIBUTES = {
        SYNC_TOKEN: '_cart_sync_token',
        LOGIN_STATE: '_cart_logged_in',
    };
}

class HttpClient {
    async request(url, options = {}) {
        const response = await fetch(url, options);

        let data = null;

        try {
            data = await response.json();
        } catch {
            data = null;
        }

        if (!response.ok) {
            console.error('[CartPersistence] Request failed:', {
                url,
                status: response.status,
                requestBody: options.body ? JSON.parse(options.body) : null,
                responseBody: data,
            });

            const message =
                data?.message ||
                data?.description ||
                data?.status ||
                `Request failed: ${response.status}`;

            throw new Error(message);
        }

        return data;
    }

    get(url) {
        return this.request(url, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
        });
    }

    post(url, body, headers = {}) {
        return this.request(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
        });
    }
}

class ShopifyCartService {
    constructor(httpClient) {
        this.http = httpClient;
    }

    async getCart() {
        return this.http.get('/cart.js');
    }

    async clearCart() {
        try {
            return await this.http.request('/cart/clear.js', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                },
            });
        } catch (error) {
            console.warn('[CartPersistence] /cart/clear.js failed. Falling back to quantity 0.', error);

            const cart = await this.getCart();

            const updates = {};

            for (const item of cart.items || []) {
                updates[item.variant_id] = 0;
            }

            if (!Object.keys(updates).length) {
                return cart;
            }

            return this.http.post('/cart/update.js', {
                updates,
            }, {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            });
        }
    }

    async updateAttributes(attributes) {
        return this.http.post('/cart/update.js', {
            attributes,
        }, {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        });
    }

    async setCartQuantities(items) {
        const validItems = items
            .map((item) => ({
                variantId: this.normalizeVariantId(item.variantId),
                quantity: Number(item.quantity),
            }))
            .filter((item) => item.variantId && item.quantity >= 0);

        if (!validItems.length) {
            return this.getCart();
        }

        const updates = {};

        for (const item of validItems) {
            updates[item.variantId] = item.quantity;
        }

        return this.http.post('/cart/update.js', {
            updates,
        }, {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        });
    }

    async addItemsToCart(items) {
        const validItems = items
            .map((item) => ({
                id: this.normalizeVariantId(item.variantId),
                quantity: Number(item.quantity),
            }))
            .filter((item) => item.id && item.quantity > 0);

        if (!validItems.length) {
            return this.getCart();
        }

        for (const item of validItems) {
            try {
                await this.http.post('/cart/add.js', {
                    id: item.id,
                    quantity: item.quantity,
                }, {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                });
            } catch (error) {
                console.error('[CartPersistence] Could not add item:', item, error);
            }
        }

        return this.getCart();
    }

    async replaceCartItems(items, attributes = {}) {
        await this.clearCart();

        if (items.length > 0) {
            try {
                await this.setCartQuantities(items);
            } catch (error) {
                console.warn('[CartPersistence] setCartQuantities failed. Falling back to addItemsToCart.', error);
                await this.addItemsToCart(items);
            }
        }

        return this.updateAttributes(attributes);
    }

    async mergeCartItems(remoteItems, attributes = {}) {
        const currentCart = await this.getCart();

        const itemsToAdd = CartMergeService.getItemsToAdd({
            localItems: currentCart.items,
            remoteItems,
        });

        await this.addItemsToCart(itemsToAdd);

        return this.updateAttributes(attributes);
    }

    normalizeVariantId(value) {
        if (!value) return null;

        const id = String(value).includes('/')
            ? String(value).split('/').pop()
            : String(value);

        return Number(id);
    }
}

class SavedCartApi {
    constructor(httpClient, config) {
        this.http = httpClient;
        this.config = config;
    }

    async getSavedCart(customerId) {
        const data = await this.http.post(
            this.config.GET_ENDPOINT,
            { customerId },
            this.config.HEADERS,
        );

        if (!data?.cart) {
            return null;
        }

        return this.normalizeSavedCart(data.cart);
    }

    async uploadCart({ customerId, token, action, items }) {
        return this.http.post(
            this.config.UPDATE_ENDPOINT,
            {
                customerId,
                token,
                action,
                lineItems: items.map((item) => ({
                    variantId: item.variantId,
                    qty: item.quantity,
                })),
            },
            this.config.HEADERS,
        );
    }

    normalizeSavedCart(cart) {
        const items = Array.isArray(cart.line_items)
            ? cart.line_items.map((item) => ({
                variantId: Number(item.variant_id),
                quantity: Number(item.qty),
            }))
            : [];

        return {
            token: cart.token || null,
            items,
        };
    }
}

class CartMergeService {
    static normalizeShopifyItems(items = []) {
        return items.map((item) => ({
            variantId: Number(item.variant_id),
            quantity: Number(item.quantity),
        }));
    }

    static getItemsToAdd({ localItems = [], remoteItems = [] }) {
        const localMap = new Map();

        for (const item of this.normalizeShopifyItems(localItems)) {
            localMap.set(Number(item.variantId), Number(item.quantity));
        }

        const itemsToAdd = [];

        for (const remoteItem of remoteItems) {
            const variantId = Number(remoteItem.variantId);
            const remoteQty = Number(remoteItem.quantity);
            const localQty = Number(localMap.get(variantId) || 0);

            if (!variantId || !remoteQty) continue;

            if (remoteQty > localQty) {
                itemsToAdd.push({
                    variantId,
                    quantity: remoteQty - localQty,
                });
            }
        }

        return itemsToAdd;
    }

    static hasItems(cart) {
        return Array.isArray(cart?.items) && cart.items.length > 0;
    }
}

class CartTokenService {
    static createToken(length = 24) {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let token = '';

        for (let i = 0; i < length; i++) {
            token += characters.charAt(
                Math.floor(Math.random() * characters.length),
            );
        }

        return token;
    }
}

class CartPersistenceManager {
    constructor({
        customerId,
        shopifyCartService,
        savedCartApi,
        config,
    }) {
        this.customerId = customerId;
        this.shopifyCart = shopifyCartService;
        this.savedCartApi = savedCartApi;
        this.config = config;
        this.isSyncing = false;
        this.uploadPromise = null;
        this.uploadQueued = false;
        this.eventsBound = false;
    }

    async init() {
        if (!this.customerId) return;
        this.bindCartChangeEvents();

        try {
            await this.syncFromRemoteCart();
        } catch (error) {
            console.error('[CartPersistence] Init error:', error);
        }
    }

    async syncFromRemoteCart() {
        if (this.isSyncing) return;

        this.isSyncing = true;

        try {
            const localCart = await this.shopifyCart.getCart();
            const savedCart = await this.savedCartApi.getSavedCart(this.customerId);

            if (!savedCart || !savedCart.token) {
                await this.uploadCurrentCart('create', { createNewToken: true });
                return;
            }

            const localSyncToken =
                localCart.attributes?.[this.config.ATTRIBUTES.SYNC_TOKEN];

            if (localSyncToken === savedCart.token) {
                return;
            }

            const wasAlreadyLoggedIn =
                localCart.attributes?.[this.config.ATTRIBUTES.LOGIN_STATE] === 'true';

            const shouldMerge =
                !wasAlreadyLoggedIn && CartMergeService.hasItems(localCart);

            const attributes = {
                ...localCart.attributes,
                [this.config.ATTRIBUTES.SYNC_TOKEN]: savedCart.token,
                [this.config.ATTRIBUTES.LOGIN_STATE]: 'true',
            };

            if (shouldMerge) {
                await this.shopifyCart.mergeCartItems(savedCart.items, attributes);
                await this.refreshCartDrawer();
                await this.uploadCurrentCart('update', { createNewToken: true });
            } else {
                await this.shopifyCart.replaceCartItems(savedCart.items, attributes);
                await this.refreshCartDrawer();
            }
        } finally {
            this.isSyncing = false;
            document.dispatchEvent(
                new CustomEvent('theme:cart:validate-business-rules')
            );
        }
    }

    async queueCartUpload() {
        if (this.isSyncing) return;

        if (this.uploadPromise) {
            this.uploadQueued = true;
            return this.uploadPromise;
        }

        this.uploadPromise = this.uploadCurrentCart('update', {
            createNewToken: true,
        });

        try {
            await this.uploadPromise;
        } finally {
            this.uploadPromise = null;

            if (this.uploadQueued) {
                this.uploadQueued = false;
                return this.queueCartUpload();
            }
        }
    }

    async refreshCartDrawer() {
        const cartItems = document.querySelector('cart-items');

        if (cartItems && typeof cartItems.getCart === 'function') {
            await cartItems.getCart();
            return;
        }

        document.dispatchEvent(
            new CustomEvent('theme:cart:refresh')
        );
    }

    async uploadCurrentCart(action = 'update', options = {}) {
        if (!this.customerId) return;

        const cart = await this.shopifyCart.getCart();

        const token = options.createNewToken
            ? CartTokenService.createToken()
            : cart.attributes?.[this.config.ATTRIBUTES.SYNC_TOKEN] || CartTokenService.createToken();

        const attributes = {
            ...cart.attributes,
            [this.config.ATTRIBUTES.SYNC_TOKEN]: token,
            [this.config.ATTRIBUTES.LOGIN_STATE]: 'true',
        };

        await this.shopifyCart.updateAttributes(attributes);

        const freshCart = await this.shopifyCart.getCart();

        const items = CartMergeService.normalizeShopifyItems(freshCart.items);

        await this.savedCartApi.uploadCart({
            customerId: this.customerId,
            token,
            action,
            items,
        });
    }

    bindCartChangeEvents() {
        if (this.eventsBound) return;

        this.eventsBound = true;

        document.addEventListener(
            'cart:persistence:upload',
            () => {
                this.queueCartUpload().catch((error) => {
                    console.error(
                        '[CartPersistence] Upload error:',
                        error
                    );
                });
            }
        );
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const httpClient = new HttpClient();

    const shopifyCartService = new ShopifyCartService(httpClient);

    const savedCartApi = new SavedCartApi(
        httpClient,
        CartPersistenceConfig,
    );

    const manager = new CartPersistenceManager({
        customerId: CartPersistenceConfig.CUSTOMER_ID,
        shopifyCartService,
        savedCartApi,
        config: CartPersistenceConfig,
    });

    manager.init();

    window.cartPersistenceManager = manager;

    window.addEventListener('pageshow', () => {
        manager.refreshCartDrawer();
    });
});